import React, { useState, useEffect, useCallback, useRef } from "react";
import {jwtDecode} from "jwt-decode";
import { fetchGetImgPerfil } from '../../peticiones_API/PeticionesAuth';
import { fetchMisPermisos } from '../../peticiones_API/PeticionesPermisos';
import { fetchMiAmbito } from '../../peticiones_API/PeticionesVacaciones';

export const UsuarioContext = React.createContext();

export const UsuarioProvider = ({ children }) => {
  const [persona, setPersona] = useState(null);
  const [permisos, setPermisos] = useState([]);
  const [permisosCargados, setPermisosCargados] = useState(false);
  // Qué módulos están montados en el servidor. Viene del MISMO sitio que los permisos, y no de
  // una `REACT_APP_...` de este build: dos variables que pueden discrepar tienen dos formas de
  // fallar, y las dos son malas —menú hacia 404, o módulo invisible que funciona—.
  //
  // Arranca VACÍO, y el vacío significa oculto: hasta que el servidor conteste no se pinta nada
  // del módulo. Es la misma regla que en el servidor —ausente es oculto— y evita el parpadeo de
  // una entrada de menú que desaparece.
  const [modulos, setModulos] = useState({});
  // ── POR QUÉ HACE FALTA SU PROPIA BANDERA, Y NO VALE `permisosCargados` ─────────────────────
  //
  // `permisosCargados` se pone a `true` TAMBIÉN cuando no hay persona todavía —«sin persona, nada
  // que esperar»—, y en ese instante `modulos` está vacío. Un gate que decidiera con ese par
  // redirigiría en el primer render, antes de que nadie hubiera preguntado nada, y la redirección
  // no tiene vuelta atrás: cuando el mapa llega, ya te has ido.
  //
  // Vacío y «todavía no lo sé» NO son lo mismo, y confundirlos es el mismo fallo que el `ambito`
  // documenta más abajo. Esta bandera se pone a `true` SÓLO cuando el servidor ha contestado.
  const [modulosCargados, setModulosCargados] = useState(false);
  // ── TU PROPIA INSIGNIA, PARA LA BARRA DE ARRIBA ───────────────────────────────────────────
  //
  // Viene del MISMO sitio que los permisos, y no del token: el JWT se firma al entrar y no sabe
  // nada de las vacaciones de hoy. Una sesión abierta desde ayer llevaría el dato de ayer.
  //
  // `null` es «sin insignia» y también «todavía no lo sé»; aquí da igual porque no decide nada:
  // es un punto de color, no una puerta. Cuando no se sabe, no se pinta.
  const [insignia, setInsignia] = useState(null);
  // Ámbito de vacaciones resuelto en servidor (GET /vacaciones/mi-ambito). Distinción
  // RATIFICADA: `ambito=null` = "NO lo sé" (sin cargar o fallo de red); un OBJETO con
  // ejes (aunque vacíos) = "sé tu ámbito". No colapsar los dos en un if truthy.
  const [ambito, setAmbito] = useState(null);
  const [ambitoCargado, setAmbitoCargado] = useState(false);
  const [ambitoError, setAmbitoError] = useState(false);
  const [imgPerfil, setImgPerfil] = useState(null);
  const [imgPerfilCargada, setImgPerfilCargada] = useState(false);
  const [cargando, setCargando] = useState(true);
  const inicializado = useRef(false);

  const checkToken = useCallback(() => {
    const token = localStorage.getItem("token");
    if (token) {
      let decoded;
      try {
        decoded = jwtDecode(token);
      } catch (error) {
        setPersona(null);
        setPermisos([]);
        setModulos({});
        setInsignia(null);
        localStorage.removeItem("token");
        localStorage.removeItem("lastActivity");
        localStorage.removeItem("numActivo");
        setCargando(false);
        return;
      }

      try {
        setPersona({
            iat: decoded?.iat,
            exp: decoded?.exp,
            id: decoded?.id,
            nombre: decoded?.nombre,
            apellidos: decoded?.apellidos,
            email: decoded?.mail_empresa,
            foto: decoded?.foto,
            rol: decoded?.rol,
            reservas: decoded?.reservas || false,
            es_supervisor: decoded?.es_supervisor || false,
            calendario_laboral_id: decoded?.calendario_laboral_id || null,
            calendario_laboral_colab_id: decoded?.calendario_laboral_colab_id || null,
            es_colaborador: decoded?.es_colaborador || false,
            empresa_logo: decoded?.empresa_logo || null,
            gestion_vacaciones_int: decoded?.gestion_vacaciones_int || null,
            gestion_vacaciones_colab: decoded?.gestion_vacaciones_colab || null,
            es_persona_colaboradora: decoded?.es_persona_colaboradora || false,
            empresa_vacaciones: decoded?.empresa_vacaciones !== false,
            empresa_nombre: decoded?.empresa_nombre || null
          });
      } catch (error) {
        setPersona(null);
        setPermisos([]);
        setModulos({});
        setInsignia(null);
        localStorage.removeItem("token");
        localStorage.removeItem("lastActivity");
        localStorage.removeItem("numActivo");
      }
    } else {
      setPersona(null);
      setPermisos([]);
      setInsignia(null);
    }
    setCargando(false);
  }, []);

  // Cargar permisos del usuario tras detectar persona válida.
  // `permisosCargados` permite a los consumidores (módulo Inventario)
  // evitar el parpadeo de "sin acceso" durante la ventana entre que el
  // JWT se decodifica y este GET resuelve.
  const cargarPermisos = useCallback(async () => {
    setPermisosCargados(false); // re-login: forzar gating en la nueva carga
    setModulosCargados(false);
    try {
      const data = await fetchMisPermisos();
      setPermisos(data?.permisos || []);
      setModulos(data?.modulos || {});
      setModulosCargados(true);
      setInsignia(data?.insignia || null);
    } catch (error) {
      console.error("Error al cargar permisos:", error);
      setPermisos([]);
      setModulos({});
      setInsignia(null);
    } finally {
      setPermisosCargados(true);
    }
  }, []);

  // Ámbito de vacaciones desde servidor. Fail-closed pero RUIDOSO (ratificado):
  //   - éxito → ambito = objeto (ejes vacíos = "no gestionas a nadie", legítimo).
  //   - fallo → ambito = null ("no lo sé") + ambitoError=true. Los consumidores cierran
  //     SOLO bandeja/aprobar/ámbito-ampliado; el módulo sigue accesible (tus propias
  //     vacaciones no dependen del ámbito) y muestran aviso de "vacaciones" + reintento.
  const cargarAmbito = useCallback(async () => {
    setAmbitoCargado(false);
    setAmbitoError(false);
    try {
      const data = await fetchMiAmbito();
      // Validación de FORMA: un 200 con cuerpo inesperado ({}, despliegue a medias, proxy
      // raro) NO debe entrar al estado LISTO y tumbar la pantalla en `ambito.ver.negocios`.
      // Exige ver/gestionar como objetos con sus arrays; si no, se trata como "no lo sé".
      const ejeValido = (e) => e && typeof e === 'object'
        && Array.isArray(e.negocios) && Array.isArray(e.colaboradoras);
      const formaValida = data && typeof data === 'object'
        && typeof data.puedeGestionar === 'boolean'   // exige el campo de A1 (backend): fuerza el orden de despliegue
        && ejeValido(data.ver) && ejeValido(data.gestionar);
      if (formaValida) {
        setAmbito(data);
      } else {
        console.error("Ámbito de vacaciones con forma inesperada:", data);
        setAmbito(null);      // forma inválida → "no lo sé", no estado LISTO
        setAmbitoError(true);
      }
    } catch (error) {
      console.error("Error al cargar el ámbito de vacaciones:", error);
      setAmbito(null);      // null = "no lo sé", NO ejes vacíos
      setAmbitoError(true);
    } finally {
      setAmbitoCargado(true);
    }
  }, []);

  const cargarImagenPerfil = useCallback(async (userId, esColab) => {
    if (imgPerfilCargada) return;

    const cacheKey = `${esColab ? 'colab' : 'int'}-${userId}`;
    const imgCache = localStorage.getItem('imgPerfilCache');
    const imgCacheTime = localStorage.getItem('imgPerfilCacheTime');
    const imgCacheUserId = localStorage.getItem('imgPerfilCacheUserId');
    const cacheExpiration = 60 * 60 * 1000; // 1 hora

    if (imgCache && imgCacheTime && imgCacheUserId === cacheKey) {
      const timeElapsed = Date.now() - parseInt(imgCacheTime);
      if (timeElapsed < cacheExpiration) {
        setImgPerfil(imgCache);
        setImgPerfilCargada(true);
        return;
      }
    }

    try {
      const response = await fetchGetImgPerfil();
      if (response && response.data && response.data.foto) {
        const ruta = esColab ? 'persona-colaboradora' : 'persona';
        const urlImagen = process.env.REACT_APP_API_URL + `/api/${ruta}/imagen/${response.data.foto}`;
        setImgPerfil(urlImagen);
        localStorage.setItem('imgPerfilCache', urlImagen);
        localStorage.setItem('imgPerfilCacheTime', Date.now().toString());
        localStorage.setItem('imgPerfilCacheUserId', cacheKey);
      } else {
        setImgPerfil(null);
        localStorage.removeItem('imgPerfilCache');
        localStorage.removeItem('imgPerfilCacheTime');
        localStorage.removeItem('imgPerfilCacheUserId');
      }
    } catch (error) {
      console.error("Error al cargar imagen de perfil:", error);
      setImgPerfil(null);
    }
    setImgPerfilCargada(true);
  }, [imgPerfilCargada]);

  useEffect(() => {
    if (!inicializado.current) {
      inicializado.current = true;
      checkToken();
    }

    const handleStorageChange = (event) => {
      if (event.key === 'token') {
        setCargando(true);
        checkToken();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [checkToken]);

  const prevUserKeyRef = useRef(null);

  useEffect(() => {
    if (persona && persona.id) {
      const userKey = `${persona.es_persona_colaboradora ? 'colab' : 'int'}-${persona.id}`;
      if (prevUserKeyRef.current !== null && prevUserKeyRef.current !== userKey) {
        setImgPerfil(null);
        setImgPerfilCargada(false);
      }
      prevUserKeyRef.current = userKey;
      cargarImagenPerfil(persona.id, persona.es_persona_colaboradora);
      cargarPermisos();
      cargarAmbito();
    } else {
      prevUserKeyRef.current = null;
      setImgPerfil(null);
      setImgPerfilCargada(false);
      setPermisos([]);
      setPermisosCargados(true); // sin persona → nada que esperar
      setAmbito(null);
      setAmbitoCargado(true);    // sin persona → nada que esperar
      setAmbitoError(false);
      localStorage.removeItem('imgPerfilCache');
      localStorage.removeItem('imgPerfilCacheTime');
      localStorage.removeItem('imgPerfilCacheUserId');
    }
  }, [persona?.id, persona?.es_persona_colaboradora, cargarImagenPerfil, cargarPermisos, cargarAmbito]);


  return (
    <UsuarioContext.Provider value={{
      persona,
      permisos,
      permisosCargados,
      // `modulos.<nombre> === true` es lo único que enciende. Cualquier otra cosa —error, módulo
      // desconocido— es oculto. Pero «todavía no lo sé» se pregunta con `modulosCargados`, NO
      // con el mapa vacío: son cosas distintas y confundirlas redirige antes de tiempo.
      modulos,
      modulosCargados,
      // Tu situación de hoy, tal y como la ve el resto. Compuesta por el servidor con la misma
      // función que la ficha, el guardado y los listados: cinco caminos, una sola regla.
      insignia,
      // ── CONTRATO DE CONSUMO DEL ÁMBITO (los 6 consumidores deciden con esto) ──
      // Decide SIEMPRE con el par (ambitoCargado, ambitoError). NUNCA con `ambito == null`:
      // null es "CARGANDO" *y* "ERROR" a la vez; confundirlos parpadea "sin acceso" en cada
      // carga de página. Receta:
      //   1) !ambitoCargado                → CARGANDO: no decidas nada (anti-blip). Espera/spinner.
      //   2) ambitoCargado && ambitoError  → NO LO SÉ: módulo ACCESIBLE; cierra SOLO bandeja/
      //      aprobar/ámbito-ampliado; aviso "vacaciones" + reintento (recargarAmbito).
      //   3) ambitoCargado && !ambitoError → LISTO: usa `ambito` (ejes vacíos = "no gestionas
      //      a nadie", legítimo, NO es error).
      ambito,
      ambitoCargado,
      ambitoError,
      imgPerfil,
      cargando,
      actualizarPersona: checkToken,
      recargarPermisos: cargarPermisos,
      recargarAmbito: cargarAmbito,
      tienePermiso: (clave) => permisos.includes(clave),
      recargarImagen: () => {
        setImgPerfilCargada(false);
        localStorage.removeItem('imgPerfilCache');
        localStorage.removeItem('imgPerfilCacheTime');
        localStorage.removeItem('imgPerfilCacheUserId');
      }
    }}>
      {children}
    </UsuarioContext.Provider>
  );
};
